<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->

<template>
  <div class="wy-analytics-contact-reasons-detail-topic-intents" v-loading="loading">
    <div class="intents-heading">
      <span class="heading-text">Intents triggered within topic ({{ tableData && tableData.length ? tableData.length : 0 }})</span>
      <el-button v-if="tableData && tableData.length" class="export-btn" round @click="handleExport">
        <div class="export-icon"></div>
        <span class="export-text">Export</span> 
      </el-button>
    </div>
    <el-table :data="tableData" ref="intentsFoundListTable" :border="true" class="intents-found-table">
      <el-table-column v-if="tableData.length > 0" show-overflow-tooltip prop="name" sortable :sort-method="sortIntents" label="Intent name" width="450"> </el-table-column>
      <el-table-column v-if="tableData.length > 0" :prop="'total'" sortable show-overflow-tooltip label="Total" width="450">
        <template slot-scope="{ row }">
          {{ formatNumber(row.total) }}
        </template>
      </el-table-column>
      <el-table-column v-if="tableData.length > 0" prop="percent" label="% of Total" sortable :sort-method="sortPercentage" width="250" label-class-name="col-label-total" class-name="col-total">
        <template slot-scope="{ row }">
          {{ formatPercentage(row.percent) }}
        </template></el-table-column
      >
    </el-table>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import ObjectsToCsv from 'objects-to-csv';
import WyUtils from '../../library/wy-utils';

export default Vue.extend({
  name: 'wy-analytics-contact-reasons-detail-topic-intents',
  props: ['parent', 'activeTab'],
  components: {},

  data() {
    return {
      module: this,
      loading: false,
      tableData: [],
      fetchData: true,
    };
  },

  computed: {
    ...mapGetters(['dataModel', 'authUser']),
  },

  methods: {
    ...mapActions(['fetchContactReasonsTopicIntents']),

    sortIntents(a, b) {
      const nameA = a.name.replace('$', '').toUpperCase();
      const nameB = b.name.replace('$', '').toUpperCase();

      if (nameA < nameB) {
        return -1;
      } else if (nameA > nameB) {
        return 1;
      } else {
        return 0;
      }
    },

    sortPercentage(a, b) {
      const nameA = Number(a.percent);
      const nameB = Number(b.percent);

      if (nameA < nameB) {
        return -1;
      } else if (nameA > nameB) {
        return 1;
      } else {
        return 0;
      }
    },

    startLoading() {
      this.initDate = new Date();
      if (!this.loading) {
        this.logEventLoaded();
      }
      this.loadTopicIntents();
    },

    logEventLoaded() {
      if (this.initDate) {
        this.$store.dispatch('amplitudeLogWithFilters', {
          name: 'intentTopics_loaded',
          data: { topic_name: this.parent.currentItem.topicDisplayName, data_load_time: WyUtils.getTimeDiff(this.initDate) },
        });
        this.initDate = null;
      }
    },

    formatNumber(value) {
      return value.toLocaleString();
    },

    formatPercentage(value) {
      return value + '%';
    },

    async handleExport() {
      const selectedColumns = ['Intent name', 'Total', '% of Total'];
      const selectedRows = this.tableData;
      const topicName = this.parent.topicName.replace(/ /g, '_');

      let exportResult = [];
      let exportRow = {};
      let value = 'N/A';

      selectedRows.map((row) => {
        selectedColumns.map((column) => {
          if (column === 'Intent name') {
            value = row.name;
          } else if (column === 'Total') {
            value = this.formatNumber(row.total);
          } else if (column === '% of Total') {
            value = this.formatPercentage(row.percent);
          }
          exportRow[column] = value;
        });
        exportResult.push(exportRow);
        exportRow = {};
      });

      const csv = new ObjectsToCsv(exportResult);
      const csvContent = await csv.toString();
      let exportedFilenmae = `${this.authUser.company.name.replace(/ /g, '_')}_Conversation_Topics_Intents_${this.dataModel.dateStart}_to_${this.dataModel.dateEnd}_${topicName}.csv`;
      let blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      let downloadLink = document.createElement('a');
      if (downloadLink.download !== undefined) {
        let url = URL.createObjectURL(blob);
        downloadLink.setAttribute('href', url);
        downloadLink.setAttribute('download', exportedFilenmae);
        downloadLink.style.visibility = 'hidden';
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      }
    },

    async loadTopicIntents() {
      if (this.fetchData && this.parent.currentItem) {
        this.fetchData = false;
        this.loading = true;

        let result = await this.fetchContactReasonsTopicIntents({ topicDisplayName: this.parent.currentItem.topicDisplayName });

        if (result && result.data) {
          this.tableData = result.data;
        } else {
          document.querySelector('.intents-found-table .el-table__header-wrapper').style['border-bottom'] = '0';
        }
      }
      this.loading = false;
      this.parent.stopLoadingByTab('intents');
    },
  },
});
</script>

<style lang="scss">
.wy-analytics-contact-reasons-detail-topic-intents {
  display: flex;
  flex-direction: column;
  color: var(--site-font-chart-color);

  .intents-heading {
    min-width: 1150px;
    margin: -25px; // remove padding from parent for border-bottom
    margin-bottom: 22px;
    padding: 16px 0px;
    border-bottom: 1px solid #e8e8e8;
    height: 40px;

    display: flex;
    align-items: center;
    justify-content: space-between;

    .heading-text {
      margin: 0px 25px; // re-add padding for text
      font-weight: 600;
    }

    .export-btn {
      margin: 0px 25px; // re-add padding for button
      padding: 6px 15px 9px 11px !important;
      border-radius: 5px;
      background: var(--site-button-secondary-btn-background-primary-color);
      color: var(--site-button-secondary-btn-text-primary-color) !important;
      border: 1px solid var(--site-button-secondary-btn-hover-border-primary-color);
      font-size: 16px !important;
    }

    .export-btn:hover {
      border: 1px var(--site-button-secondary-btn-hover-border-primary-color) solid !important;
      background-color: var(--site-button-secondary-btn-hover-background-primary-color) !important;
      border-radius: 5px;
      color: var(--site-button-secondary-btn-text-primary-color) !important;
    }
    .export-btn:active {
      background-color: var(--site-button-secondary-btn-pressed-background-primary-color) !important;
    }
    .export-btn:focus {
      border: 1px var(--site-button-secondary-btn-hover-border-primary-color) solid !important;
      border-radius: 5px;
      color: var(--site-button-secondary-btn-text-primary-color) !important;
    }
    .export-icon {
      background: var(--icon_action_export);
      height: 17px;
      width: 13px;
      position: relative;
      top: 2px;
      height: 17px;
      width: 13px;
      display: inline-block;
      background-repeat: no-repeat;
    }
    .export-text {
      position: relative;
      bottom: 1px;
      left: 5px;
    }
  }

  .intents-found-table::before {
    background-color: transparent !important;
  }
  .intents-found-table {
    min-width: 1100px;
    background: #ffffff 0% 0% no-repeat padding-box;
    box-shadow: 0px 1px 2px #0000000f;
    opacity: 1;

    padding: 0px;
    margin: 0px;

    color: var(--site-font-primary-color);
    font-size: 12px;
    border-radius: 10px;

    .el-table {
      font-size: 12px;
    }
    th,
    td {
      border: 0px;
      border-bottom: 0px solid #ebeef5 !important;
      padding: 8px 0px 8px 0px;
      color: var(--site-font-primary-color);
    }
    th,
    tr {
      .cell {
        padding-left: 34px !important;
        line-height: unset;
        cursor: default;
      }
    }
    th.el-table_2_column_2 {
      .cell {
        padding-left: 20px !important;
      }
    }
    .el-table__header-wrapper {
      border-bottom: 1px solid #f1f2f2;
      .el-table__header {
        width: 100% !important;
      }
    }
    .col-label-total div.cell {
      text-align: right;
      padding-right: 100px;
    }
    .col-total div.cell {
      text-align: center;
      padding-right: 100px;
    }
    .el-table__body {
      width: 100% !important;
    }
    .el-table__body tr.el-table__row--striped td {
      background-color: #ffffff;
    }

    .el-table__empty-block {
      display: none;
      height: 288px !important;
      width: 100% !important;
      .no-data {
        text-align: center;
        display: block;
        border: none;
        height: 83px;
        margin-top: 0px;
        background-position-x: 0px;
        background-repeat: no-repeat;
        background-size: 165px 73px;
        background-image: url('../../assets/icons/icon_entities.svg');
      }
      .no-data-title {
        width: 160px;
        // margin-left: 11%;
        margin-top: 0px;
        text-align: center;
        font-size: 12px;
        color: var(--site-font-primary-color) !important;
        font-weight: 500;
      }
      .no-data-content-first-row {
        width: 160px;
        line-height: 20px;
        color: var(--site-font-primary-color) !important;
      }
      .no-data-content-second-row {
        width: 160px;
        line-height: 20px;
        padding-top: 13px;
        color: var(--site-font-primary-color) !important;
      }
    }
  }
  .el-table__body-wrapper {
    position: sticky !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
  }
}
</style>
